import React, { useState, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { http, formatErr, fmtDateShort } from "@/lib/api";
import { useAuth, can, roleLabel } from "@/lib/auth";
import { Plus, X, Trash, ListChecks, NotePencil, Lock, Globe, CheckCircle, Circle, User as UserIcon } from "@phosphor-icons/react";

const TABS = [
  { key: "all", label: "All visible" },
  { key: "mine", label: "Created by me" },
  { key: "assigned", label: "Assigned to me" },
];

export default function Tasks() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const canPublic = can(user, "tasks", "create");

  const { data: tasks = [] } = useQuery({ queryKey: ["tasks"], queryFn: () => http.get("/tasks").then(r => r.data) });
  const { data: users = [] } = useQuery({ queryKey: ["users"], queryFn: () => http.get("/users").then(r => r.data).catch(() => []) });

  const [tab, setTab] = useState("all");
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({ kind: "task", title: "", date: "", visibility: "private", assignee_id: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const filtered = useMemo(() => tasks.filter(t => {
    if (tab === "mine") return t.created_by_id === user.id;
    if (tab === "assigned") return t.assignee_id === user.id;
    return true;
  }), [tasks, tab, user.id]);

  const openModal = () => { setForm({ kind: "task", title: "", date: "", visibility: canPublic ? "public" : "private", assignee_id: "" }); setErr(""); setModal(true); };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const body = {
        kind: form.kind, title: form.title, visibility: form.visibility,
        date: form.date ? new Date(form.date).toISOString() : null,
        assignee_id: form.assignee_id || null,
      };
      await http.post("/tasks", body);
      qc.invalidateQueries({ queryKey: ["tasks"] });
      setModal(false);
    } catch (ex) { setErr(formatErr(ex?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const toggle = async (t) => {
    try { await http.post(`/tasks/${t.id}/toggle`); qc.invalidateQueries({ queryKey: ["tasks"] }); }
    catch (ex) { alert(formatErr(ex?.response?.data?.detail)); }
  };
  const remove = async (t) => {
    if (!window.confirm("Delete this item?")) return;
    try { await http.delete(`/tasks/${t.id}`); qc.invalidateQueries({ queryKey: ["tasks"] }); }
    catch (ex) { alert(formatErr(ex?.response?.data?.detail)); }
  };

  return (
    <div className="p-8 max-w-5xl mx-auto">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="overline">Planner</div>
          <h1 className="text-3xl font-heading font-semibold tracking-tight mt-1">Tasks &amp; reminders</h1>
          <p className="text-sm text-slate-600 mt-1">Non-job tasks and day notes. Public items are visible to everyone; private only to you (and anyone you assign).</p>
        </div>
        <button onClick={openModal} data-testid="task-new-button" className="inline-flex items-center gap-2 h-10 px-4 rounded-md text-white text-sm font-medium" style={{ background: "#002FA7" }}>
          <Plus size={14} weight="bold" /> New
        </button>
      </div>

      <div className="flex gap-1 mb-4 border-b border-slate-200">
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`task-tab-${t.key}`}
            className={`px-3 py-2 text-sm border-b-2 -mb-px transition-colors ${tab === t.key ? "border-[#002FA7] text-slate-900 font-medium" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="rmms-card p-6 text-sm text-slate-500" data-testid="tasks-empty">No items here yet.</div>
      ) : (
        <div className="space-y-2" data-testid="tasks-list">
          {filtered.map(t => {
            const canMod = t.created_by_id === user.id || can(user, "tasks", "edit");
            return (
              <div key={t.id} data-testid={`task-row-${t.id}`} className="rmms-card rmms-card-hover p-3.5 flex items-center gap-3">
                {t.kind === "task" ? (
                  <button onClick={() => toggle(t)} data-testid={`task-toggle-${t.id}`} className="shrink-0">
                    {t.done ? <CheckCircle size={20} weight="fill" className="text-emerald-500" /> : <Circle size={20} className="text-slate-300 hover:text-slate-400" />}
                  </button>
                ) : (
                  <NotePencil size={18} className="text-amber-500 shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <div className={`text-sm font-medium truncate ${t.done ? "line-through text-slate-400" : "text-slate-800"}`}>{t.title}</div>
                  <div className="flex items-center gap-2 mt-0.5 text-[11px] text-slate-500 flex-wrap">
                    {t.date && <span className="font-mono-data">{fmtDateShort(t.date)}</span>}
                    {t.visibility === "public"
                      ? <span className="inline-flex items-center gap-0.5 text-blue-600"><Globe size={10} weight="fill" /> Public</span>
                      : <span className="inline-flex items-center gap-0.5 text-amber-600"><Lock size={10} weight="fill" /> Private</span>}
                    {t.assignee_name && <span className="inline-flex items-center gap-0.5"><UserIcon size={10} /> {t.assignee_name}</span>}
                    <span className="text-slate-400">· by {t.created_by_name}</span>
                  </div>
                </div>
                <span className="text-[10px] font-heading uppercase tracking-wider px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 shrink-0">{t.kind}</span>
                {canMod && (
                  <button onClick={() => remove(t)} data-testid={`task-delete-${t.id}`} className="text-slate-400 hover:text-rose-600 shrink-0"><Trash size={15} /></button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {modal && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center p-4 z-50" data-testid="task-modal">
          <div className="bg-white border border-slate-200 rounded-md w-full max-w-md p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-heading font-semibold tracking-tight">New item</h2>
              <button type="button" onClick={() => setModal(false)} className="h-8 w-8 rounded-sm hover:bg-slate-100 flex items-center justify-center"><X size={16} /></button>
            </div>
            <form onSubmit={submit} className="space-y-4">
              <div className="inline-flex rounded-md border border-slate-200 overflow-hidden text-sm">
                <button type="button" onClick={() => setForm({ ...form, kind: "task" })} data-testid="task-kind-task"
                  className={`px-3 py-1.5 inline-flex items-center gap-1.5 ${form.kind === "task" ? "bg-[#002FA7] text-white" : "text-slate-600 hover:bg-slate-50"}`}><ListChecks size={13} /> Task</button>
                <button type="button" onClick={() => setForm({ ...form, kind: "note" })} data-testid="task-kind-note"
                  className={`px-3 py-1.5 inline-flex items-center gap-1.5 border-l border-slate-200 ${form.kind === "note" ? "bg-[#002FA7] text-white" : "text-slate-600 hover:bg-slate-50"}`}><NotePencil size={13} /> Note</button>
              </div>
              <div>
                <label className="overline mb-1.5 block">{form.kind === "task" ? "Task title" : "Note"} *</label>
                <input required value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} data-testid="task-title-input" className="rmms-input" />
              </div>
              <div>
                <label className="overline mb-1.5 block">Date {form.kind === "note" ? "(pin to day)" : "(due)"}</label>
                <input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} data-testid="task-date-input" className="rmms-input" />
              </div>
              <div>
                <label className="overline mb-1.5 block">Assign to (optional)</label>
                <select value={form.assignee_id} onChange={e => setForm({ ...form, assignee_id: e.target.value })} data-testid="task-assignee-select" className="rmms-input">
                  <option value="">(no one)</option>
                  {users.map(u => <option key={u.id} value={u.id}>{u.name} · {roleLabel(u.role)}</option>)}
                </select>
              </div>
              <div>
                <label className="overline mb-1.5 block">Visibility</label>
                <div className="inline-flex rounded-md border border-slate-200 overflow-hidden text-sm">
                  <button type="button" onClick={() => setForm({ ...form, visibility: "private" })} data-testid="task-vis-private"
                    className={`px-3 py-1.5 inline-flex items-center gap-1.5 ${form.visibility === "private" ? "bg-amber-50 text-amber-700 font-medium" : "text-slate-500 hover:bg-slate-50"}`}><Lock size={12} /> Private</button>
                  {canPublic && (
                    <button type="button" onClick={() => setForm({ ...form, visibility: "public" })} data-testid="task-vis-public"
                      className={`px-3 py-1.5 inline-flex items-center gap-1.5 border-l border-slate-200 ${form.visibility === "public" ? "bg-blue-50 text-blue-700 font-medium" : "text-slate-500 hover:bg-slate-50"}`}><Globe size={12} /> Public</button>
                  )}
                </div>
                {!canPublic && <p className="text-[11px] text-slate-400 mt-1">You can create private items. Public items require additional permission.</p>}
              </div>
              {err && <div className="text-sm text-rose-600 border-l-2 border-rose-500 pl-3 py-1" data-testid="task-error">{err}</div>}
              <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
                <button type="button" onClick={() => setModal(false)} className="h-10 px-4 text-sm border border-slate-200 rounded-md">Cancel</button>
                <button type="submit" disabled={busy} data-testid="task-submit-button" className="h-10 px-4 rounded-md text-white text-sm" style={{ background: "#002FA7" }}>{busy ? "Saving…" : "Create"}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
